import React, { useEffect, useState, useCallback } from 'react';
import { getEmployees, getScreenshots, getSettings } from '../api';
import { formatDateTime } from '../utils';
import { useAuth } from '../AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const PAGE = 60;

// Build a screenshot URL. Newer uploads store a full path/URL in `filename`
// (e.g. "/uploads/x.png" or an R2 "https://…" URL); legacy ones store a bare
// filename served under /screenshots/.
const shotUrl = (f) => {
  if (!f) return '';
  if (/^https?:\/\//i.test(f)) return f;
  if (f.startsWith('/')) return `${API_BASE}${f}`;
  return `${API_BASE}/screenshots/${f}`;
};

const rangeToDates = (r) => {
  const now = new Date();
  const sod = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  if (r === 'today') return { from: sod(now).toISOString() };
  if (r === 'yesterday') { const y = new Date(now); y.setDate(y.getDate() - 1); return { from: sod(y).toISOString(), to: sod(now).toISOString() }; }
  if (r === 'week') { const w = new Date(now); w.setDate(w.getDate() - 7); return { from: w.toISOString() }; }
  if (r === 'month') return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString() };
  return {}; // all time
};

function Toggle({ on, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!on)} aria-pressed={on} aria-label="Idle screenshots only"
      style={{ width: 42, height: 24, borderRadius: 999, border: '1px solid var(--glass-border)', padding: 0,
        background: on ? 'var(--primary-strong)' : 'var(--glass-2)', position: 'relative', cursor: 'pointer', transition: '.15s' }}>
      <span style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: '50%',
        background: '#fff', transition: '.15s', boxShadow: '0 1px 3px rgba(0,0,0,.25)' }} />
    </button>
  );
}

export default function Screenshots() {
  const { user, isAdmin, isManager } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmp, setSelectedEmp] = useState('');   // '' = whole team (admin/manager)
  const [range, setRange] = useState('all');
  const [idleOnly, setIdleOnly] = useState(false);
  const [screenshots, setScreenshots] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [allowed, setAllowed] = useState(true); // permission gate for plain employees
  const [searchInput, setSearchInput] = useState(''); // text box value
  const [search, setSearch] = useState('');            // debounced value used for fetching

  useEffect(() => {
    const setup = async () => {
      if (isManager) { setEmployees(await getEmployees()); return; } // default '' = their team
      if (!isAdmin && user?.employee_id) {
        try {
          const settings = await getSettings();
          const canSee = settings.perm_employee_see_screenshots === 'true' || settings.perm_employee_see_screenshots === true;
          setAllowed(canSee);
          if (!canSee) return;
        } catch {}
        setEmployees([]);
        setSelectedEmp(user.employee_id); // employees are locked to themselves
        return;
      }
      setEmployees(await getEmployees()); // admin — default '' = whole team
    };
    setup();
  }, [isAdmin, isManager, user?.employee_id]);

  const fetchPage = useCallback(async (off, replace) => {
    if (!isAdmin && !isManager && !selectedEmp) return; // plain employee needs own id
    setLoading(true);
    try {
      const { from, to } = rangeToDates(range);
      const params = { limit: PAGE, offset: off };
      if (selectedEmp) params.employee_id = selectedEmp;
      if (from) params.from = from;
      if (to) params.to = to;
      if (idleOnly) params.idle = 1;
      if (search.trim()) params.q = search.trim();
      const data = await getScreenshots(params);
      setScreenshots(prev => (replace ? data : [...prev, ...data]));
      setOffset(off + data.length);
      setHasMore(data.length === PAGE);
    } catch { if (replace) setScreenshots([]); }
    setLoading(false);
  }, [selectedEmp, range, idleOnly, search, isAdmin, isManager]);

  useEffect(() => { fetchPage(0, true); }, [fetchPage]);

  // Debounce the search box so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Keyboard nav for the preview lightbox: ← prev, → next, Esc close.
  useEffect(() => {
    if (!preview) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { setPreview(null); return; }
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      setPreview(cur => {
        if (!cur) return cur;
        const i = screenshots.findIndex(s => s.id === cur.id);
        const ni = e.key === 'ArrowLeft' ? i - 1 : i + 1;
        return (ni >= 0 && ni < screenshots.length) ? screenshots[ni] : cur;
      });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview, screenshots]);

  const labelStyle = { display: 'flex', flexDirection: 'column', gap: 5, fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)' };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Screenshots</h1>
      </div>

      {!allowed && !isAdmin && (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>🔒</div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Access Restricted</div>
          <div className="text-muted">Your administrator has not enabled screenshot viewing for employees. Contact your admin to request access.</div>
        </div>
      )}

      {(allowed || isAdmin) && <>
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            {(isAdmin || isManager) && (
              <label style={{ ...labelStyle, minWidth: 210 }}>
                View screenshots for
                <select value={selectedEmp} onChange={e => setSelectedEmp(e.target.value)}
                  style={{ padding: '9px 12px', border: '1px solid var(--glass-border)', borderRadius: 12, fontSize: 14, background: 'var(--glass-2)', color: 'var(--text)' }}>
                  <option value="">👥 Team — everyone</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
              </label>
            )}
            <label style={{ ...labelStyle, minWidth: 150 }}>
              Time range
              <select value={range} onChange={e => setRange(e.target.value)}
                style={{ padding: '9px 12px', border: '1px solid var(--glass-border)', borderRadius: 12, fontSize: 14, background: 'var(--glass-2)', color: 'var(--text)' }}>
                <option value="all">All time</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="week">This week</option>
                <option value="month">This month</option>
              </select>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginTop: 18 }}>
              <Toggle on={idleOnly} onChange={setIdleOnly} />
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-2)' }}>Idle only</span>
            </div>
            <label style={{ ...labelStyle, flex: 1, minWidth: 200 }}>
              Search app or site
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--muted)' }}>🔍</span>
                <input
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder="e.g. whatsapp, upwork, chrome…"
                  style={{ width: '100%', padding: '9px 32px 9px 34px', border: '1px solid var(--glass-border)', borderRadius: 12, fontSize: 14, background: 'var(--glass-2)', color: 'var(--text)' }}
                />
                {searchInput && (
                  <button onClick={() => setSearchInput('')} aria-label="Clear search"
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted)', fontSize: 16 }}>×</button>
                )}
              </div>
            </label>
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: 12 }}>
            📸 Captured automatically by the <strong>Desktop App</strong> while clocked in. Change frequency in <strong>Settings → Tracking</strong> (Screenshot Interval) — applies to everyone.
          </div>
        </div>

        <div className="card">
          <div className="card-title">
            {idleOnly ? 'Idle screenshots' : 'Captured screenshots'} ({screenshots.length}{hasMore ? '+' : ''})
          </div>
          {loading && screenshots.length === 0 ? (
            <div className="empty">Loading…</div>
          ) : screenshots.length === 0 ? (
            <div className="empty">No {idleOnly ? 'idle ' : ''}screenshots found for this selection.</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                {screenshots.map(ss => (
                  <div key={ss.id} style={{ border: '1px solid var(--glass-border)', borderRadius: 14, overflow: 'hidden', cursor: 'pointer', background: 'var(--glass-2)' }}
                    onClick={() => setPreview(ss)}>
                    <img src={shotUrl(ss.filename)} alt="screenshot"
                      style={{ width: '100%', height: 160, objectFit: 'cover', background: 'var(--hairline)', display: 'block' }}
                      onError={e => { e.target.style.display = 'none'; }} />
                    <div style={{ padding: '9px 11px' }}>
                      {ss.app_name && (
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--primary-strong, #4f46e5)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={ss.app_title || ss.app_name}>
                          🖥️ {ss.app_name}
                        </div>
                      )}
                      <div style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--text)' }}>{ss.employee_name}</div>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>{formatDateTime(ss.captured_at)}</div>
                      <div style={{ fontSize: '0.75rem', marginTop: 2, color: 'var(--text-2)' }}>Activity: <strong style={{ color: (ss.activity_score || 0) <= 15 ? '#b45309' : 'var(--text)' }}>{ss.activity_score}%</strong></div>
                      <div className="text-muted" style={{ fontSize: '0.72rem', marginTop: 2 }}>⌨️ {ss.keypress_count || 0} keys · 🖱️ {ss.mouse_count || 0} clicks</div>
                    </div>
                  </div>
                ))}
              </div>
              {hasMore && (
                <div style={{ textAlign: 'center', marginTop: 18 }}>
                  <button className="btn btn-outline" disabled={loading} onClick={() => fetchPage(offset, false)}>
                    {loading ? 'Loading…' : 'View more'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {preview && (() => {
          const idx = screenshots.findIndex(s => s.id === preview.id);
          const hasPrev = idx > 0;
          const hasNext = idx >= 0 && idx < screenshots.length - 1;
          const go = (delta) => (e) => { e.stopPropagation(); const ni = idx + delta; if (ni >= 0 && ni < screenshots.length) setPreview(screenshots[ni]); };
          const arrowBtn = (side, on, handler) => (
            <button onClick={handler} disabled={!on} aria-label={side === 'left' ? 'Previous' : 'Next'}
              style={{
                position: 'absolute', top: '50%', transform: 'translateY(-50%)', [side]: 16,
                width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: on ? 'pointer' : 'default',
                background: 'rgba(255,255,255,.92)', color: '#1e1b4b', fontSize: 26, fontWeight: 700, lineHeight: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 14px rgba(0,0,0,.25)', opacity: on ? 1 : 0.35,
              }}>
              {side === 'left' ? '‹' : '›'}
            </button>
          );
          return (
            <div className="modal-overlay" onClick={() => setPreview(null)}>
              {arrowBtn('left', hasPrev, go(-1))}
              {arrowBtn('right', hasNext, go(1))}
              <div style={{ position: 'relative', background: 'var(--glass-2)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', border: '1px solid var(--glass-border)', borderRadius: 20, padding: 20, maxWidth: '90vw', maxHeight: '90vh', color: 'var(--text)' }} onClick={e => e.stopPropagation()}>
                <button onClick={() => setPreview(null)} aria-label="Close"
                  style={{ position: 'absolute', top: 12, right: 12, width: 30, height: 30, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--row-hover)', color: 'var(--text)', fontSize: 18, lineHeight: 1 }}>×</button>
                <div style={{ fontWeight: 700, marginBottom: 10, paddingRight: 34 }}>
                  {preview.employee_name} · {formatDateTime(preview.captured_at)}
                  {preview.app_name && <span style={{ color: 'var(--primary-strong, #4f46e5)' }}> · 🖥️ {preview.app_name}</span>}
                </div>
                <img src={shotUrl(preview.filename)} alt="screenshot" style={{ maxWidth: '80vw', maxHeight: '70vh', objectFit: 'contain', borderRadius: 12 }} />
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <span>Activity: <strong>{preview.activity_score}%</strong> · ⌨️ {preview.keypress_count || 0} keys · 🖱️ {preview.mouse_count || 0} clicks</span>
                  {idx >= 0 && <span className="text-muted" style={{ fontSize: '0.8rem' }}>{idx + 1} of {screenshots.length}</span>}
                </div>
              </div>
            </div>
          );
        })()}
      </>}
    </div>
  );
}
